import { isNotNil } from 'es-toolkit';
import {
  catchError,
  filter,
  from,
  map,
  merge,
  shareReplay,
  Subject,
  tap,
  type ObservableInput,
  type OperatorFunction,
} from 'rxjs';

import { withVisibilityChange } from '../rxjs/with-visibility-change.operator';

interface SuccessResult<T> {
  value: Awaited<T> | T;
  iteration: number;
  success: true;
}

interface ErrorResult {
  error: unknown;
  retryCount: number;
  iteration: number;
  success: false;
}

interface GeneratorOptions {
  delay: number;
  maxRetries: number;
  shouldContinue: () => boolean;
}

export type PromiseGenerator<T> = ReturnType<typeof createPromiseGenerator<T>>;

/**
 * Create an [AsyncGenerator](https://developer.mozilla.org/de/docs/Web/JavaScript/Reference/Statements/function*)
 * from a next() Promise-factory such as the once found in Atruvia `publicApi`.
 */
export async function* createPromiseGenerator<T>(
  promiseFactory: () => Promise<T>,
  options: Partial<GeneratorOptions> = {}
) {
  const { delay = 0, maxRetries = -1, shouldContinue = () => true } = options;

  let retryCount = 0;
  let iterationCount = 0;

  while (shouldContinue()) {
    try {
      // Execute the promise factory to get the next promise
      const result = await promiseFactory();

      // Reset retry counter on success
      retryCount = 0;
      iterationCount++;

      // Yield both the result and metadata
      yield {
        value: result,
        iteration: iterationCount,
        success: true,
      } satisfies SuccessResult<T>;

      // Optional delay between iterations
      if (delay > 0) await new Promise(resolve => setTimeout(resolve, delay));
    } catch (error) {
      retryCount++;

      // Yield the error information
      yield {
        error,
        retryCount,
        iteration: iterationCount,
        success: false,
      } satisfies ErrorResult;

      // Check if we exceeded max retries
      if (maxRetries >= 0 && retryCount > maxRetries) {
        return { stopped: true, reason: 'max-retries-exceeded' };
      }

      // Apply delay on error if specified
      if (delay > 0) await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  return { stopped: true, reason: 'condition-met', completedIterations: iterationCount };
}

/** Operator to throw proper errors, originally caught by the generator. */
const handleError = <T>(): OperatorFunction<SuccessResult<T> | ErrorResult, T> =>
  map(result => {
    if (result.success) {
      return result.value;
    } else {
      throw result.error;
    }
  });

export interface AtruviaPromises<T> {
  init: () => Promise<T>;
  concurrent: () => Promise<T>;
}

/**
 * Turns Atruvia get() and getNext() pattern into an rxjs Observable.
 *
 * Note: There are various APIs available on the window object
 * at `window.okpGlobal.portalContext.consentInformationService`.
 *
 * Use of these APIs is discouraged while they remain undocumented and unofficial.
 * Instead use this service with Atruvia Promise-factories.
 */
export class AtruviaPromiseService<T> {
  #state?: T;

  public state = () => this.#state;

  public stateSubject$;

  public destroy$;

  constructor({ init, concurrent }: AtruviaPromises<T>) {
    this.destroy$ = new Subject<void>();

    // check current state on init
    const init$ = from(init()).pipe(
      map<T, SuccessResult<T>>(result => ({ value: result, iteration: 0, success: true })),
      catchError<SuccessResult<T>, ObservableInput<ErrorResult>>(async err => {
        return { error: err, iteration: 0, retryCount: 0, success: false };
      })
    );
    // continously receive updates
    const concurrent$ = from(createPromiseGenerator(concurrent));

    this.stateSubject$ = merge(init$, concurrent$).pipe(
      withVisibilityChange(), // prevent memory-leaks when tab is hidden or browser is moved to background
      handleError(), // throw proper errors
      filter(result => isNotNil(result)), // ensure no empty results
      tap({ next: result => (this.#state = result) }), // save current state
      shareReplay({ refCount: true, bufferSize: 1 }) // ensure subscribers receive last value on subscribtion
    );
  }
}
